import React from 'react'
import { Button, Input, Modal, Select, Space, Tag, message, Popconfirm, Table, Card, Tabs } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons'
import API from '../../../../utils/api'
import { useToken } from '../../../hooks'
import EntryTime from '../../../components/PositionColumns/EntryTime'
import useSWR from 'swr'
import UserOTPSetting from './UserOTPSetting'

const defaultEntries = [
	{ key: '', type: 'IP', memo: '' },
	{ key: '', type: 'DEVICE', memo: '' },
]

const PartnerPCSetting = () => {
	const [modalVisible, setModalVisible] = React.useState(false)
	const [partnerId, setPartnerId] = React.useState('')
	const [entries, setEntries] = React.useState([...defaultEntries])
	const [editingId, setEditingId] = React.useState<number | null>(null)
	const [editKey, setEditKey] = React.useState('')
	const [editMemo, setEditMemo] = React.useState('')

	const { data: token } = useToken()

	const apiKey = `/admin/partnerpc?page=1&maxData=500&t=`
	const { data, mutate } = useSWR<any>(
		token ? apiKey : null,
		async () => {
			const response = await API.get(apiKey, token + '', {})
			return response?.result?.result || response?.result || {}
		}
	)

	const list = data?.list || []

	const grouped = React.useMemo(() => {
		const map: Record<number, { items: any[], fullname: string, nickname: string, loginid: string }> = {}
		list.forEach((item: any) => {
			if (!map[item.partner_id]) {
				map[item.partner_id] = {
					items: [],
					fullname: item.fullname || '',
					nickname: item.nickname || '',
					loginid: item.loginid || '',
				}
			}
			map[item.partner_id].items.push(item)
		})
		return Object.entries(map).sort(([a], [b]) => Number(a) - Number(b))
	}, [list])

	const addEntry = () => {
		setEntries([...entries, { key: '', type: 'IP', memo: '' }])
	}

	const removeEntry = (index) => {
		if (entries.length <= 1) return
		setEntries(entries.filter((_, i) => i !== index))
	}

	const updateEntry = (index, field, value) => {
		const newEntries = [...entries]
		newEntries[index] = { ...newEntries[index], [field]: value }
		setEntries(newEntries)
	}

	const handleRegister = async () => {
		if (!partnerId) {
			message.error('파트너 ID를 입력하세요.')
			return
		}
		const validEntries = entries.filter(e => e.key.trim())
		if (validEntries.length === 0) {
			message.error('최소 1개의 IP 또는 기기키를 입력하세요.')
			return
		}
		for (const entry of validEntries) {
			await API.send('POST', '/admin/partnerpc', token + '', {
				partner_id: Number(partnerId),
				key: entry.key.trim(),
				type: entry.type,
				memo: entry.memo,
			})
		}
		message.success(`${validEntries.length}건 등록되었습니다.`)
		setModalVisible(false)
		setPartnerId('')
		setEntries([...defaultEntries])
		mutate()
	}

	const handleSaveEdit = async (record: any) => {
		if (editKey !== record.key) {
			await API.send('DELETE', '/admin/partnerpc', token + '', { id: record.id })
			await API.send('POST', '/admin/partnerpc', token + '', {
				partner_id: record.partner_id,
				key: editKey.trim(),
				type: record.type,
				memo: editMemo,
			})
		} else {
			await API.send('PUT', '/admin/partnerpc', token + '', {
				id: record.id,
				memo: editMemo,
			})
		}
		message.success('수정되었습니다.')
		setEditingId(null)
		mutate()
	}

	const handleDelete = async (id: number) => {
		await API.send('DELETE', '/admin/partnerpc', token + '', { id })
		message.success('삭제되었습니다.')
		mutate()
	}

	const columns = [
		{
			title: '타입',
			dataIndex: 'type',
			key: 'type',
			width: 80,
			align: 'center' as const,
			render: (text) => <Tag color={text === 'IP' ? 'blue' : 'green'}>{text}</Tag>,
		},
		{
			title: '키 (IP/기기)',
			dataIndex: 'key',
			key: 'key',
			render: (text, record) =>
				editingId === record.id ? (
					<input
						value={editKey}
						onChange={e => setEditKey(e.target.value)}
						style={{ fontSize: 12, width: '100%', padding: '2px 6px', border: '1px solid #d9d9d9', borderRadius: 4 }}
					/>
				) : (
					<span style={{ fontSize: 12, wordBreak: 'break-all' as const }}>{text}</span>
				),
		},
		{
			title: '메모',
			dataIndex: 'memo',
			key: 'memo',
			width: 180,
			render: (text, record) =>
				editingId === record.id ? (
					<input
						value={editMemo}
						onChange={e => setEditMemo(e.target.value)}
						style={{ fontSize: 12, width: '100%', padding: '2px 6px', border: '1px solid #d9d9d9', borderRadius: 4 }}
					/>
				) : (
					text || ''
				),
		},
		{
			title: '등록일',
			dataIndex: 'created_at',
			key: 'created_at',
			width: 160,
			align: 'center' as const,
			render: (text) => <EntryTime time={text} />,
		},
		{
			title: '관리',
			key: 'action',
			width: 110,
			align: 'center' as const,
			render: (_, record) =>
				editingId === record.id ? (
					<Space>
						<Button size="small" type="primary" icon={<SaveOutlined />}
							onClick={() => handleSaveEdit(record)} />
						<Button size="small" onClick={() => setEditingId(null)}>취소</Button>
					</Space>
				) : (
					<Space>
						<Button size="small" icon={<EditOutlined />}
							onClick={() => {
								setEditingId(record.id)
								setEditKey(record.key)
								setEditMemo(record.memo || '')
							}} />
						<Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)}>
							<Button size="small" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Space>
				),
		},
	]

	const ipDeviceContent = (
		<div>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
				<h3 style={{ margin: 0 }}>파트너 IP/기기 관리 <Tag>{list.length}건</Tag></h3>
				<Space>
					<Button icon={<ReloadOutlined />} onClick={() => mutate()}>새로고침</Button>
					<Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
						등록
					</Button>
				</Space>
			</div>

			{grouped.length === 0 && (
				<Card><span style={{ color: '#999' }}>등록된 파트너 IP/기기가 없습니다.</span></Card>
			)}

			{grouped.map(([pid, { items, fullname, nickname, loginid }]) => (
				<Card
					key={pid}
					title={
						<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
							<Tag color="purple">ID: {pid}</Tag>
							{fullname && <Tag>실명: {fullname}</Tag>}
							{nickname && <Tag>닉네임: {nickname}</Tag>}
							{loginid && <Tag>로그인: {loginid}</Tag>}
							<Tag>{items.length}건</Tag>
							<Button
								size="small"
								type="primary"
								icon={<PlusOutlined />}
								style={{ marginLeft: 8 }}
								onClick={() => {
									setPartnerId(pid)
									setEntries([{ key: '', type: 'IP', memo: '' }])
									setModalVisible(true)
								}}
							/>
						</span>
					}
					size="small"
					style={{ marginBottom: 12 }}
				>
					<Table
						dataSource={items}
						columns={columns}
						rowKey="id"
						size="small"
						pagination={false}
					/>
				</Card>
			))}

			<Modal
				title="파트너 IP/기기 등록"
				open={modalVisible}
				okText="등록"
				cancelText="취소"
				width={640}
				onOk={handleRegister}
				onCancel={() => {
					setModalVisible(false)
					setPartnerId('')
					setEntries([...defaultEntries])
				}}>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
					<input
						value={partnerId}
						onChange={e => setPartnerId(e.target.value)}
						placeholder="파트너 ID"
						type="number"
						style={{ width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' as const }}
					/>
					{entries.map((entry, index) => (
						<div key={index} style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'center' }}>
							<Select
								value={entry.type}
								onChange={v => updateEntry(index, 'type', v)}
								style={{ width: 110 }}>
								<Select.Option value="IP">IP</Select.Option>
								<Select.Option value="DEVICE">DEVICE</Select.Option>
							</Select>
							<input
								value={entry.key}
								onChange={e => updateEntry(index, 'key', e.target.value)}
								placeholder={entry.type === 'IP' ? 'IP 주소' : '기기키'}
								style={{ flex: 1, padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14 }}
							/>
							<input
								value={entry.memo}
								onChange={e => updateEntry(index, 'memo', e.target.value)}
								placeholder="메모"
								style={{ width: 140, padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4, fontSize: 14 }}
							/>
							{entries.length > 1 && (
								<Button size="small" danger icon={<DeleteOutlined />}
									onClick={() => removeEntry(index)} />
							)}
						</div>
					))}
					<Button type="dashed" onClick={addEntry} icon={<PlusOutlined />} block>
						항목 추가
					</Button>
				</div>
			</Modal>
		</div>
	)

	return (
		<div style={{ padding: '16px 0' }}>
			<Tabs
				defaultActiveKey="ip-device"
				items={[
					{
						key: 'ip-device',
						label: 'IP/기기',
						children: ipDeviceContent,
					},
					{
						key: 'otp',
						label: 'OTP 관리',
						children: <UserOTPSetting />,
					},
				]}
			/>
		</div>
	)
}

export default React.memo(PartnerPCSetting)
